import { Suspense, use, useMemo } from "react";
import { SchemaV4PageRenderer } from "@cogo/react-renderer/adapters/schema-v4-page";
import { loadPageTree } from "../lib/capture-loader";
import { ProvenanceFooter } from "../components/ProvenanceFooter";

interface StorefrontProps {
  cartCount: number;
  onNavigate: (path: string) => void;
  onAddToCart: (productId: string) => void;
  setSelectedProductId: (id: string) => void;
}

function StorefrontInner({
  cartCount,
  onNavigate,
  onAddToCart,
  setSelectedProductId,
}: StorefrontProps) {
  const page = use(loadPageTree("storefront"));

  // Deep clone and update the Cogo JSON in memory with the dynamic cart counter.
  const pageWithState = useMemo(() => {
    const cloned = JSON.parse(JSON.stringify(page));
    const findAndMutate = (node: any) => {
      if (!node) return;
      if (node.id === "nav-cart-btn") {
        node.text = `Cart (${cartCount})`;
      }
      if (node.children) {
        node.children.forEach(findAndMutate);
      }
    };
    findAndMutate(cloned.root);
    return cloned;
  }, [page, cartCount]);

  const handleEvent = (elementId: string, eventType: string) => {
    if (eventType === "tap") {
      if (
        elementId === "nav-link-shop" ||
        elementId === "hero-btn" ||
        elementId === "logo-text" ||
        elementId === "logo-mark"
      ) {
        onNavigate("/storefront");
      } else if (elementId === "nav-cart-btn") {
        onNavigate("/checkout");
      } else if (elementId === "product-buy-1") {
        onAddToCart("product-1");
      } else if (elementId === "product-buy-2") {
        onAddToCart("product-2");
      } else if (elementId === "product-buy-3") {
        onAddToCart("product-3");
      } else if (
        elementId === "product-1" ||
        elementId === "product-img-1" ||
        elementId === "product-name-1"
      ) {
        setSelectedProductId("product-1");
        onNavigate("/product-detail");
      } else if (
        elementId === "product-2" ||
        elementId === "product-img-2" ||
        elementId === "product-name-2"
      ) {
        setSelectedProductId("product-2");
        onNavigate("/product-detail");
      } else if (
        elementId === "product-3" ||
        elementId === "product-img-3" ||
        elementId === "product-name-3"
      ) {
        setSelectedProductId("product-3");
        onNavigate("/product-detail");
      } else if (elementId === "nav-link-about") {
        alert(
          "Cogo is a next-generation AI no-code platform that generates high-fidelity Flutter and Web applications from natural language.",
        );
      }
    }
  };

  return (
    <>
      <SchemaV4PageRenderer
        page={pageWithState}
        dispatchEvent={handleEvent}
        navigate={onNavigate}
        colorScheme="dark"
      />
      <ProvenanceFooter />
    </>
  );
}

export function Storefront(props: StorefrontProps) {
  return (
    <Suspense fallback={<div className="p-8 text-stone-500 bg-slate-950 min-h-screen text-center">Loading Storefront…</div>}>
      <StorefrontInner {...props} />
    </Suspense>
  );
}
